import {Component, HostListener, OnInit} from '@angular/core';
import {formatCurrency} from '@angular/common';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {SubscribedComponent} from '../../subscribed.component';
import {QuoteService} from '../../shared/quote.service';
import {NotifierService} from '../../shared/notifier.service';
import {fieldError, fieldErrors} from '../../shared/invoicing.service';
import {QUOTE_MANUAL, QUOTE_TEXTS, Quote, QuotePerson, QuotePriceLine, QuoteStatus} from '../../interfaces/quote';
import {UserRef} from '../../interfaces/user';

// Half up in whole cents, as (int)round($x * 100) on the server; toPrecision drops the float noise in 1.005 * 100.
export function cents(value: string | number | null): number {
  return Math.round(Number((Number(value) * 100).toPrecision(12)));
}

// As QuotePerson::amountCents(): hours and rate in whole cents first, so 1.5 x 33.33 is 50.00, not 49.99.
export function personCents(p: QuotePerson): number | null {
  return p.hours == null || p.hours === '' || p.rate == null || p.rate === '' ? null
    : Math.floor((cents(p.hours) * cents(p.rate) + 50) / 100);
}

// A price line the server accepts (QuoteController::update rules).
export function validLine(l: QuotePriceLine): boolean {
  const amount = l.amount == null || l.amount === '' ? NaN : Number(l.amount);
  return !!l.description?.trim() && l.description.length <= 500 && amount >= 0 && amount <= 9999999999;
}

/**
 * One quote (#1697): its header, recipient, texts per chapter, the people with their rate and hours or the
 * price lines, and the total excl. VAT. The PDF prints the stored quote, so a download saves first.
 */
@Component({
  selector: 'bst-quote',
  templateUrl: './quote.component.html',
  styleUrls: ['./quote.component.scss'],
  standalone: false
})
export class QuoteComponent extends SubscribedComponent implements OnInit {
  readonly slots = QUOTE_TEXTS;
  readonly statuses = QUOTE_MANUAL;
  readonly personCents = personCents;
  quote: Quote = null;
  failed = false;
  busy = false;
  // Why the server refused the last PDF; hidden again once the quote is edited.
  pdfRefusal: string = null;
  errors: Record<string, string[]> = {};
  members: UserRef[] = [];
  adding: number = null;
  // The status picked in the select; set back to the quote's own when a change fails.
  pick: QuoteStatus = null;
  private saved: string = null;
  private loads = 0;

  constructor(private route: ActivatedRoute, private router: Router, private quotes: QuoteService,
              private notifier: NotifierService, private translate: TranslateService) {
    super();
  }

  get lang(): string {
    return this.translate.currentLang ?? this.translate.defaultLang;
  }

  ngOnInit() {
    // Active members of the company; the server refuses anyone else.
    this.quotes.members().then(m => this.members = m).catch(() => this.members = []);
    this.subs.push(this.route.paramMap.subscribe(async params => {
      const load = ++this.loads;
      this.quote = null;
      this.failed = false;
      const quote = await this.quotes.getQuote(+params.get('id')).catch(() => null);
      // A later navigation won, also one back to this same quote: this answer is stale.
      if (this.loads !== load) { return; }
      this.failed = !quote;
      this.show(quote);
    }));
  }

  private show(quote: Quote) {
    this.quote = quote;
    this.saved = JSON.stringify(quote);
    this.pick = quote?.status ?? null;
    this.errors = {};
  }

  get isDirty(): boolean {
    return !!this.quote && JSON.stringify(this.quote) !== this.saved;
  }

  async canLeave(): Promise<boolean> {
    if (!this.isDirty) { return true; }
    return this.notifier.confirm('quotes.confirm_leave', null, 'quotes.leave', 'danger', 'cancel');
  }

  @HostListener('window:beforeunload', ['$event'])
  unload(event: BeforeUnloadEvent) {
    if (this.isDirty || this.busy) { event.preventDefault(); event.returnValue = true; }
  }

  error(field: string): string | null {
    return fieldError(this.translate, this.errors, field);
  }

  name(p: QuotePerson): string {
    const u = p.user ?? this.members.find(m => m.id === p.User_id);
    return u ? `${u.name_first} ${u.name_last}`.trim() : `#${p.User_id}`;
  }

  // Only a Draft can be edited (the server answers 409 otherwise); back to Draft unlocks it.
  get locked(): boolean {
    return this.quote.status !== 'draft';
  }

  get byHours(): boolean {
    return this.quote.pricing === 'hours';
  }

  // Priced by hours, the PDF needs hours and a rate for everyone on it (the server refuses with a 422 otherwise).
  get unpriced(): string[] {
    return this.byHours ? this.quote.people.filter(p => personCents(p) === null).map(p => this.name(p)) : [];
  }

  get totalCents(): number {
    return this.byHours
      ? this.quote.people.reduce((sum, p) => sum + (personCents(p) ?? 0), 0)
      : this.quote.price_lines.reduce((sum, l) => sum + cents(l.amount), 0);
  }

  money(c: number | null): string {
    return c === null || Number.isNaN(c) ? '' : formatCurrency(c / 100, this.lang, '€', 'EUR');
  }

  // A stored number as typed in the UI language, so the inputs match the amounts: a decimal comma in Dutch.
  decimal(value: string | number | null): string {
    return value == null ? '' : this.lang === 'nl' ? String(value).replace('.', ',') : String(value);
  }

  // What the server takes: digits and a decimal point, whichever separator was typed.
  typedDecimal(typed: string): string {
    return typed.replace(/[^\d.,]/g, '').replace(',', '.');
  }

  get candidates(): UserRef[] {
    return this.members.filter(m => !this.quote.people.some(p => p.User_id === m.id));
  }

  addPerson(id: number) {
    const m = this.members.find(u => u.id === id);
    if (m) { this.quote.people.push({User_id: m.id, Submission_id: null, hours: null, rate: null, user: m}); }
    setTimeout(() => this.adding = null);
  }

  removePerson(i: number) {
    this.quote.people.splice(i, 1);
  }

  addLine() {
    this.quote.price_lines.push({description: '', amount: null});
  }

  removeLine(i: number) {
    this.quote.price_lines.splice(i, 1);
  }

  // The whole quote: people and price lines are replaced as sent. Switching pricing keeps both lists.
  async save(): Promise<boolean> {
    if (this.busy) { return false; }
    this.busy = true;
    this.errors = {};
    const q = this.quote;
    // Priced by hours the lines only wait for a switch back; one the server would refuse would block the save unseen.
    const lines = q.price_lines.filter(l => !this.byHours || validLine(l));
    const body: Partial<Quote> = {
      title: q.title, responsible_User_id: q.responsible_User_id, quote_date: q.quote_date, expires_on: q.expires_on,
      recipient_name: q.recipient_name, recipient_address: q.recipient_address, addressee: q.addressee, salutation: q.salutation,
      description: q.description, texts: q.texts, pricing: q.pricing, start_date: q.start_date || null, end_date: q.end_date || null,
      people: q.people.map(p => ({User_id: p.User_id, Submission_id: p.Submission_id, hours: p.hours === '' ? null : p.hours, rate: p.rate === '' ? null : p.rate})),
      price_lines: lines.map(l => ({description: l.description, amount: l.amount})),
    };
    const before = JSON.stringify(q);
    const saved = await this.quotes.save(q.id, body).catch(e => { this.errors = fieldErrors(e); return null; });
    this.busy = false;
    if (!saved) { return false; }
    // Moved on to another quote meanwhile: nothing here to update.
    if (this.quote !== q) { return true; }
    // Typed while saving: keep those edits on screen, dirty against what the server now holds.
    if (JSON.stringify(this.quote) !== before) { this.saved = JSON.stringify({...JSON.parse(before), price_lines: lines}); return true; }
    this.show(saved);
    return true;
  }

  async download() {
    // Still dirty after the save: typed meanwhile, and the PDF would print the older text.
    if (this.isDirty && (!await this.save() || this.isDirty)) { return; }
    this.busy = true;
    this.pdfRefusal = null;
    await this.quotes.downloadPdf(this.quote.id).catch(e => this.pdfRefusal = e?.reason ?? null);
    this.busy = false;
  }

  // A Draft with edits is saved first, so leaving Draft does not lock them out unsaved.
  async changeStatus(status: QuoteStatus) {
    const id = this.quote.id;
    if (this.busy || status === this.quote.status) { return; }
    const revert = () => setTimeout(() => this.pick = this.quote?.status ?? null);
    if (this.isDirty && (!await this.save() || this.isDirty)) { revert(); return; }
    this.busy = true;
    const updated = await this.quotes.setStatus(id, status).catch(() => null);
    this.busy = false;
    // Moved on to another quote meanwhile: nothing here to update.
    if (this.quote?.id !== id) { return; }
    if (updated) { this.show(updated); } else { revert(); }
  }

  async remove() {
    if (!await this.notifier.confirm('quotes.delete_confirm', this.quote.number, 'quotes.delete', 'danger', 'cancel')) { return; }
    this.busy = true;
    const done = await this.quotes.remove(this.quote.id).then(() => true).catch(() => false);
    this.busy = false;
    if (done) {
      const opp = this.quote.Opportunity_id;
      this.quote = null;
      void this.router.navigate(['/opportunities', opp]);
    }
  }
}
